import { ReactNode } from 'react';
import HomeV2Background, { BackgroundFunctionKey } from './HomeV2Background.js';
import HomeV2Header, { HomeV2HeaderPlaceholder } from './HomeV2Header.js';
import HomeV2Footer, { HomeV2FooterPlaceholder } from './HomeV2Footer.js';
import type { NavItemId } from './HomeV2Footer.js';
import type { TimeOfDayConfig } from './timeOfDayConfig.js';

interface HomeV2LayoutProps {
  children?: ReactNode;
  /**
   * Active tab to highlight in the footer
   * If not provided, defaults to 'daily-sparks'
   */
  activeTab?: NavItemId;
  /** Time-of-day configuration for dynamic colors */
  timeOfDayConfig?: TimeOfDayConfig;
  /**
   * Content container style
   * - 'transparent': Content sits directly on gradient (default, for Home)
   * - 'white-container': White card container on gradient (for Activity Details)
   */
  contentStyle?: 'transparent' | 'white-container';

  /***
   * padding for contentStyle 'transparent'
   * 
   */
  transparentContainerXPadding?: number;
  /**
   * Max width classes for content container
   * Defaults: 'max-w-md lg:max-w-xl' (transparent) or 'max-w-md lg:max-w-[640px]' (white-container)
   */
  maxWidthClass?: string;
  /**
   * Whether to show the cloud logo in the header
   * Defaults to false
   */
  showCloudLogo?: boolean;
  /**
   * Whether to show night sky decorations (stars, glow)
   * Defaults to timeOfDayConfig.showNightDecorations if not provided
   */
  showNightDecorations?: boolean;
  /**
   * Whether to show morning glow overlay
   * Defaults to timeOfDayConfig.showMorningGlow if not provided
   */
  showMorningGlow?: boolean;
  /** Custom CSS gradient string that completely overrides the default background gradient layers. */
  backgroundGradient?: string;
  /** Whether the check-in transition is active (triggers fade-out of header/footer) */
  isTransitioning?: boolean;

  /** the function to use to generate custom background gradient */
  gradientFunction?: BackgroundFunctionKey;
    /** Show user avatar on the right side of the header component*/
  showAvatar?: boolean;

  /** Click handler for the cloud mascot in the header */
  onCloudClick?: () => void;
}

/**
 * HomeV2Layout Component
 *
 * Common layout for Home V2 and Activity Details screens with:
 * - Fixed header at top
 * - Scrollable gradient background with time-of-day theming
 * - Fixed footer at bottom
 * - Support for transparent content (Home) or white container (Activity Details)
 *
 * Background and content scroll together under fixed header.
 * Mobile-optimized with responsive max-widths.
 *
 * Usage:
 * // Home screen (transparent, content on gradient)
 * <HomeV2Layout activeTab="daily-sparks" timeOfDayConfig={config}>
 *   <YourContent />
 * </HomeV2Layout>
 *
 * // Activity Details (white container)
 * <HomeV2Layout
 *   contentStyle="white-container"
 *   showNightDecorations={false}
 *   timeOfDayConfig={config}
 * >
 *   <ActivityContent />
 * </HomeV2Layout>
 */
export default function HomeV2Layout({
  children,
  activeTab,
  timeOfDayConfig,
  contentStyle = 'transparent',
  transparentContainerXPadding = 4,
  maxWidthClass,
  showCloudLogo = false,
  showNightDecorations,
  showMorningGlow,
  backgroundGradient,
  isTransitioning = false,
  gradientFunction = 'default',
  showAvatar = true,
  onCloudClick,
}: HomeV2LayoutProps) {

  // Determine max-width class based on content style if not explicitly provided
  const contentMaxWidth = maxWidthClass ?? (
    contentStyle === 'white-container'
      ? 'max-w-md lg:max-w-[640px]'
      : 'max-w-md lg:max-w-xl'
  );

  // Use explicit decoration props if provided, otherwise fall back to timeOfDayConfig
  const showNightDecorationsValue = showNightDecorations ?? timeOfDayConfig?.showNightDecorations;
  const showMorningGlowValue = showMorningGlow ?? timeOfDayConfig?.showMorningGlow;

  return (
    <div className="min-h-screen relative" style={{ overflow: 'hidden' }}>
      {/* Fixed Header - Top */}
      <HomeV2Header
        showCloudLogo={showCloudLogo}
        backgroundColor={timeOfDayConfig?.headerBackground}
        isTransitioning={isTransitioning}
        showAvatar={showAvatar}
        onCloudClick={onCloudClick}
        // showCloudLogo={showCloudLogo}
        // headerGradient={timeOfDayConfig?.headerGradient}
        // headerTextColor={timeOfDayConfig?.headerTextColor}
        // headerDecoration={timeOfDayConfig?.headerDecoration}
        // isTransitioning={isTransitioning}
        // onCloudClick={onCloudClick}
      />
      {/* Header placeholder - to offset fixed header height for layout */}
      <HomeV2HeaderPlaceholder />

      {/* Scrollable Background with Content */}
      <HomeV2Background
        timeOfDayConfig={timeOfDayConfig}
        backgroundGradient={backgroundGradient}
        showNightDecorations={showNightDecorationsValue}
        showMorningGlow={showMorningGlowValue}
        isTransitioning={isTransitioning}
        gradientFunction={gradientFunction}
      >
        <main
          className="overflow-x-hidden flex flex-col"
          style={{
           minHeight: '100vh',
          }}
        >
          {contentStyle === 'transparent' ? (
            // Transparent content container (Home screen)
            <>
              <div className={`${contentMaxWidth} mx-auto px-${transparentContainerXPadding} w-full flex-1 flex flex-col`}>
                {children}
              </div>
              {/* Footer placeholder outside content */}
              <HomeV2FooterPlaceholder />
            </>
          ) : (
            // White container (Activity Details screen)
            <div className={`${contentMaxWidth} mx-auto`} style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
              {children}
              {/* Footer placeholder inside white container */}
              <HomeV2FooterPlaceholder />
            </div>
          )}
        </main>
      </HomeV2Background>
      {/* Fixed Footer - Bottom */}
      <HomeV2Footer activeTab={activeTab} isTransitioning={isTransitioning} />
    </div>
  );
}
